import { kpiAdapter } from './kpiAdapter';
import { RenderAdapterError } from '../types';
import type {
  KpiBannerAdapterConfig,
  KpiBannerAdapterProps,
  KpiBannerRows,
  RenderAdapterResult,
} from '../types';
import type { TransformRow } from '../../transforms/types';

function isRowArray(value: unknown): value is readonly TransformRow[] {
  return Array.isArray(value);
}

/**
 * Rows for one tile, out of the per-tile map the banner is given.
 *
 * A tile with no entry is not an error: banner tiles are fetched
 * independently, so at any moment some of them have results and some do not,
 * and the tile renders its own no-value state until its rows arrive.
 *
 * An entry that is present but is not a row array is a different thing, and
 * previously took the same path -- coerced to empty, so a host storing a string
 * under a tile id got the same silent no-value state as a tile still waiting.
 * Absent is tolerated because it is expected; wrong-typed is reported because
 * nothing but a mistake produces it.
 */
function rowsForTile(
  rows: KpiBannerRows,
  id: string,
): { rows: readonly TransformRow[]; malformed: boolean } {
  const candidate = rows[id];
  if (candidate === undefined) return { rows: [], malformed: false };
  return isRowArray(candidate)
    ? { rows: candidate, malformed: false }
    : { rows: [], malformed: true };
}

/**
 * A banner of KPI tiles, each projected from its own rows.
 *
 * Input is keyed by tile id rather than being one row set, because each tile is
 * a separate query — different metric, dimension, or term — and merging them
 * would require a shared row shape that does not exist.
 *
 * Delegates every tile to {@link kpiAdapter} instead of repeating the
 * projection. A banner tile and a standalone tile render the same component,
 * and the two paths having drifted is what made a KPI figure mean one thing on
 * its own and another in a row of them.
 */
export function kpiBannerAdapter(
  rows: unknown,
  config: KpiBannerAdapterConfig,
): RenderAdapterResult<KpiBannerAdapterProps> {
  // A map keyed by tile id is the only input shape that can serve a banner. An
  // array is the shape every other adapter takes, so it is the mistake worth
  // naming rather than treating as empty.
  const keyedByTile =
    typeof rows === 'object' && rows !== null && !Array.isArray(rows);

  const byTile: KpiBannerRows = keyedByTile ? (rows as KpiBannerRows) : {};

  const results = config.tiles.map(({ id, ...tileConfig }) => {
    const tileRows = rowsForTile(byTile, id);
    const result = kpiAdapter(tileRows.rows, tileConfig);
    return tileRows.malformed
      ? { ...result, error: new RenderAdapterError('malformed_rows') }
      : result;
  });

  // A tile's rejection is the banner's rejection. Reading only `.props` off each
  // delegate discarded it, so a tile handed rows the tile adapter had already
  // refused rendered as a label with no number -- indistinguishable from a
  // metric whose query has not come back yet, which is a state this banner
  // deliberately supports. Absent rows stay that state; rejected rows are a
  // caller error and have to survive the delegation.
  const tileError = results.find((result) => result.error)?.error;

  // The wrong container shape, reported the same way, since a banner handed an
  // array has no tile to attribute the failure to.
  const shapeError =
    rows !== undefined && !keyedByTile
      ? new RenderAdapterError('malformed_rows')
      : undefined;

  const error = shapeError ?? tileError;

  return {
    props: {
      ...config.presentation?.passthrough,
      tiles: results.map((result) => result.props),
    },
    // Reported rather than thrown, and paired with an error the way every other
    // adapter pairs them: a bare `malformed` flag left callers branching on a
    // boolean with no message to show.
    ...(error ? { malformed: true, error } : {}),
  };
}
